import { useCallback, useMemo, useState } from "react";
import type { Announcement } from "@excalidraw-ms/shared";
import type { BackendCapabilityState } from "../auth/msalClient";
import { ANNOUNCEMENTS } from "../announcements";
import { readDismissed, writeDismissed } from "../utils/announcementDismissal";

interface UseAnnouncementsOptions {
  accountId: string | null | undefined;
  capabilityState: BackendCapabilityState;
  /** Override the feed in tests. */
  feed?: Announcement[];
  /** Override the clock in tests. */
  now?: () => number;
}

interface UseAnnouncementsResult {
  /** The single announcement to render, or null. */
  announcement: Announcement | null;
  dismiss: (id: string) => void;
}

function matchesAudience(
  announcement: Announcement,
  capabilityState: BackendCapabilityState,
): boolean {
  const audience = announcement.audience ?? "entitled";
  if (audience === "all") return true;
  if (audience === "entitled") return capabilityState === "full";
  return capabilityState === "local-only";
}

function isLive(announcement: Announcement, nowMs: number): boolean {
  if (!announcement.expiresAt) return true;
  const expiry = Date.parse(announcement.expiresAt);
  // An unparseable date must not silently retire the entry.
  if (Number.isNaN(expiry)) return true;
  return expiry > nowMs;
}

/**
 * Selects the one announcement to show, if any.
 *
 * Only one renders at a time: the footer is not a changelog, and stacking
 * entries would push the canvas off screen. Ties break on newest publish date.
 *
 * Nothing is shown while capability is still resolving, so the banner cannot
 * appear and then vanish once the check completes.
 *
 * See docs/designs/message-taxonomy.md §§ 4.2, 5.3.
 */
export function useAnnouncements({
  accountId,
  capabilityState,
  feed = ANNOUNCEMENTS,
  now = Date.now,
}: UseAnnouncementsOptions): UseAnnouncementsResult {
  const [dismissedInSession, setDismissedInSession] = useState<string[]>([]);

  const dismiss = useCallback(
    (id: string) => {
      setDismissedInSession((current) => (current.includes(id) ? current : [...current, id]));
      writeDismissed(accountId, id);
    },
    [accountId],
  );

  const announcement = useMemo(() => {
    if (!accountId) return null;
    // Nothing is announced until capability resolves, so the banner cannot
    // appear and then vanish; and nothing is announced during an outage, where
    // "this now works" would contradict the notice directly above it (§ 5.3.2).
    if (capabilityState === "checking" || capabilityState === "temporarily-unavailable") {
      return null;
    }

    const nowMs = now();
    const dismissed = new Set([...readDismissed(accountId), ...dismissedInSession]);

    const eligible = feed.filter(
      (entry) =>
        !dismissed.has(entry.id) && isLive(entry, nowMs) && matchesAudience(entry, capabilityState),
    );

    if (eligible.length === 0) return null;

    return eligible.reduce((best, entry) => {
      const bestPriority = best.priority ?? 0;
      const entryPriority = entry.priority ?? 0;
      if (entryPriority !== bestPriority) return entryPriority > bestPriority ? entry : best;
      return Date.parse(entry.publishedAt) > Date.parse(best.publishedAt) ? entry : best;
    });
  }, [accountId, capabilityState, dismissedInSession, feed, now]);

  return { announcement, dismiss };
}
